// repo/primary-care-connect/frontend/src/pages/Portal/ClinicAdmin/ClinicAdminLayout.tsx

import React, { useEffect, useMemo, useRef, useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { clinicAdminApi } from "../../../api/clinicAdmin";
import { pickFirstString } from "../../../utils/clinicAdminHelpers";
import {
  getStoredClinicAdminClinicId,
  setStoredPortalRoleChoice,
} from "../../../utils/portalRoleChoice";
import { Skeleton } from "./components/Skeleton";
import { ToastProvider } from "./components/ToastContext";

import "./ClinicAdminLayout.css";

type ClinicHeaderInfo = {
  clinicName: string;
};

type SignedInUserInfo = {
  email: string | null;
};

function cls(...parts: Array<string | false | null | undefined>) {
  return parts.filter(Boolean).join(" ");
}

function normalizeStatus(status: string): string {
  const s = String(status || "").trim();
  if (!s) return "";

  const k = s.toLowerCase();

  if (k === "closedacceptedelsewhere") return "ClosedAcceptedElsewhere";
  if (k === "in_review") return "InReview";
  if (k === "notaccepted") return "Declined";

  return s;
}

function getItemsFromResponse(res: unknown): unknown[] {
  return Array.isArray((res as any)?.items) ? (res as any).items : [];
}

function countSubmittedRequestsFromResponse(res: unknown): number {
  const items = getItemsFromResponse(res);

  return items.filter((item) => {
    const status = normalizeStatus((item as any)?.status || "").toLowerCase();
    return status === "submitted";
  }).length;
}

export default function ClinicAdminLayout() {
  const signOutHref = useMemo(() => {
    const postLogout = encodeURIComponent(`${window.location.origin}/`);
    return `/.auth/logout?post_logout_redirect_uri=${postLogout}`;
  }, []);

  const isMountedRef = useRef(true);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [clinic, setClinic] = useState<ClinicHeaderInfo | null>(null);

  const [user, setUser] = useState<SignedInUserInfo>({ email: null });

  // Unified sidebar badge for Clinic Admin Patient Requests.
  // Public/direct + navigator-forwarded requests are now treated as one inbox.
  const [patientRequestBadgeCount, setPatientRequestBadgeCount] = useState<
    number | null
  >(null);

  const [hasMultipleRoles, setHasMultipleRoles] = useState(false);

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  async function refreshPatientRequestBadgeCount() {
    if (getStoredClinicAdminClinicId() === null) return;

    try {
      const res = await clinicAdminApi.listPatientRequests({
        status: "Submitted",
        limit: 200,
        sourceScope: "all",
      } as any);

      if (!isMountedRef.current) return;

      setPatientRequestBadgeCount(countSubmittedRequestsFromResponse(res));
    } catch {
      // Badge is optional UI. If the API fails, keep the portal usable.
      if (!isMountedRef.current) return;
      setPatientRequestBadgeCount(null);
    }
  }

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;

    async function loadClinic() {
      const selectedClinicId = getStoredClinicAdminClinicId();
      if (selectedClinicId === null) {
        window.location.replace("/portal/select-role");
        return;
      }

      setStoredPortalRoleChoice("clinicadmin", selectedClinicId);

      try {
        const data = await clinicAdminApi.getClinicProfile();
        if (!mounted) return;

        // portal-clinic-admin-clinic-profile returns: { clinic: { name: ... } }
        const clinicName =
          pickFirstString((data as any)?.clinic, ["name", "Name"]) || "";

        setClinic({
          clinicName,
        });
      } catch (err: any) {
        if (!mounted) return;

        if (err?.status === 403) {
          setError("You are not authorized to access this clinic.");
        } else {
          setError("Failed to load clinic context.");
        }
      } finally {
        if (mounted) setLoading(false);
      }
    }

    loadClinic();

    return () => {
      mounted = false;
    };
  }, []);

  // Load signed-in user identity (email) from Azure Static Web Apps auth.
  useEffect(() => {
    let mounted = true;

    async function loadSignedInUser() {
      try {
        const resp = await fetch("/.auth/me", { credentials: "include" });
        if (!resp.ok) return;

        const json = await resp.json();
        if (!mounted) return;

        const principal = Array.isArray(json?.clientPrincipal)
          ? json.clientPrincipal[0]
          : json?.clientPrincipal;

        const claimsObject = principal?.claims?.reduce?.((acc: any, c: any) => {
          if (c?.typ && c?.val) acc[c.typ] = c.val;
          return acc;
        }, {});

        const email =
          pickFirstString(principal, ["userDetails", "email"]) ||
          pickFirstString(claimsObject, [
            "emails",
            "email",
            "preferred_username",
            "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress",
          ]);

        setUser({ email });
      } catch {
        // silent: email is optional UI enhancement
      }
    }

    loadSignedInUser();

    return () => {
      mounted = false;
    };
  }, []);

  // Check if user has multiple roles so the Switch Role link only appears when useful.
  useEffect(() => {
    let mounted = true;

    async function checkRoles() {
      try {
        const res = await fetch("/api/portal-auth-bootstrap", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
        });

        if (!res.ok || !mounted) return;

        const data = await res.json();
        const roles = Array.isArray(data?.roles) ? data.roles : [];

        const activeCount = roles.filter((r: any) => {
          const v = r?.isActive;
          if (v === undefined || v === null) return true;
          if (typeof v === "boolean") return v;
          if (typeof v === "number") return v === 1;
          return String(v).toLowerCase() === "true";
        }).length;

        if (mounted) setHasMultipleRoles(activeCount > 1);
      } catch {
        // silent — Switch Role link stays hidden if check fails
      }
    }

    checkRoles();

    return () => {
      mounted = false;
    };
  }, []);

  // Fetch unified pending/submitted request count for sidebar badge.
  useEffect(() => {
    refreshPatientRequestBadgeCount();

    const intervalId = window.setInterval(() => {
      refreshPatientRequestBadgeCount();
    }, 60_000);

    function handleVisibilityChange() {
      if (document.visibilityState === "visible") {
        refreshPatientRequestBadgeCount();
      }
    }

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    function handleResize() {
      if (window.innerWidth > 900) {
        setMobileMenuOpen(false);
      }
    }

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setMobileMenuOpen(false);
      }
    }

    window.addEventListener("resize", handleResize);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("resize", handleResize);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  function closeMobileMenu() {
    setMobileMenuOpen(false);
  }

  function toggleMobileMenu() {
    setMobileMenuOpen((prev) => !prev);
  }

  function toggleSidebarCollapsed() {
    setSidebarCollapsed((prev) => !prev);
  }

  if (loading) {
    return (
      <div
        className="clinic-admin-loading"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 12,
          alignItems: "center",
          padding: "40px 24px",
        }}
      >
        <Skeleton width="180px" height="20px" />
        <Skeleton width="120px" height="14px" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="clinic-admin-error">
        <h2>Access problem</h2>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <ToastProvider>
      <div
        className={cls(
          "clinic-admin-shell",
          sidebarCollapsed && "clinic-admin-shell--sidebar-collapsed"
        )}
      >
        <aside
          className={cls(
            "clinic-admin-sidebar",
            sidebarCollapsed && "clinic-admin-sidebar--collapsed",
            mobileMenuOpen && "clinic-admin-sidebar--mobile-open"
          )}
          aria-label="Clinic Admin portal navigation"
        >
          <div className="clinic-admin-sidebar-header">
            <div className="clinic-admin-sidebar-collapse-card">
              <button
                type="button"
                className="clinic-admin-sidebar-collapse-button"
                aria-label={
                  sidebarCollapsed
                    ? "Expand Clinic Admin sidebar"
                    : "Collapse Clinic Admin sidebar"
                }
                aria-expanded={!sidebarCollapsed}
                onClick={toggleSidebarCollapsed}
                title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              >
                <span aria-hidden="true">{sidebarCollapsed ? "›" : "‹"}</span>
              </button>
            </div>

            <div className="clinic-admin-sidebar-top">
              <div className="clinic-admin-sidebar-mobile-bar">
                <div className="clinic-admin-sidebar-identity-card">
                  <div className="clinic-admin-sidebar-identity">
                    <div className="clinic-admin-clinic-name">
                      {clinic?.clinicName || "Clinic"}
                    </div>
                    <div className="clinic-admin-role">
                      Clinic Admin Portal
                    </div>
                  </div>
                </div>

                <button
                  type="button"
                  className="clinic-admin-mobile-menu-button"
                  aria-expanded={mobileMenuOpen}
                  aria-controls="clinic-admin-portal-nav"
                  aria-label={
                    mobileMenuOpen
                      ? "Collapse navigation menu"
                      : "Expand navigation menu"
                  }
                  onClick={toggleMobileMenu}
                >
                  <span className="clinic-admin-mobile-menu-button-text">
                    Menu
                  </span>
                  <span
                    className={cls(
                      "clinic-admin-mobile-menu-button-chevron",
                      mobileMenuOpen &&
                        "clinic-admin-mobile-menu-button-chevron--open"
                    )}
                    aria-hidden="true"
                  >
                    ▾
                  </span>
                </button>
              </div>
            </div>
          </div>

          <div
            id="clinic-admin-portal-nav"
            className={cls(
              "clinic-admin-sidebar-accordion",
              mobileMenuOpen && "clinic-admin-sidebar-accordion--open"
            )}
          >
            <nav className="clinic-admin-nav" aria-label="Clinic Admin workspace">
              <div className="clinic-admin-nav-section">
                <div className="clinic-admin-nav-label">Workspace</div>

                <NavLink
                  to="/portal/clinic-admin/dashboard"
                  end
                  className={({ isActive }) =>
                    cls("nav-item", isActive && "active")
                  }
                  onClick={closeMobileMenu}
                  title="Dashboard"
                >
                  <span className="nav-item-label">Dashboard</span>
                </NavLink>

                <NavLink
                  to="/portal/clinic-admin/patient-requests"
                  className={({ isActive }) =>
                    cls("nav-item", isActive && "active")
                  }
                  onClick={closeMobileMenu}
                  title="Patient Requests"
                >
                  <span className="nav-item-label">Patient Requests</span>

                  {patientRequestBadgeCount !== null &&
                    patientRequestBadgeCount > 0 && (
                      <span
                        className="nav-badge"
                        aria-label={`${patientRequestBadgeCount} new patient requests`}
                        title={`${patientRequestBadgeCount} new patient requests`}
                      >
                        {patientRequestBadgeCount}
                      </span>
                    )}
                </NavLink>

                <NavLink
                  to="/portal/clinic-admin/providers/family-doctors"
                  className={({ isActive }) =>
                    cls("nav-item", isActive && "active")
                  }
                  onClick={closeMobileMenu}
                  title="Manage Providers"
                >
                  <span className="nav-item-label">Manage Providers</span>
                </NavLink>

                <NavLink
                  to="/portal/clinic-admin/oht-messages"
                  className={({ isActive }) =>
                    cls("nav-item", isActive && "active")
                  }
                  onClick={closeMobileMenu}
                  title="OHT Messages"
                >
                  <span className="nav-item-label">OHT Messages</span>
                </NavLink>

                <a
                  href="https://referral.acceptingmd.ca"
                  className="nav-item"
                  onClick={closeMobileMenu}
                  title="Referral Navigator"
                >
                  <span className="nav-item-label">Referral Navigator</span>
                </a>

                {/*
                  Navigator Requests is intentionally not shown as a separate
                  primary sidebar item anymore. Clinic Admin Patient Requests
                  will become the unified inbox, with source filtering handled
                  inside the page.

                  Keep the existing /portal/clinic-admin/navigator-requests
                  route available until App.tsx is updated to redirect it safely.
                */}
              </div>

              <div className="clinic-admin-nav-section">
                <div className="clinic-admin-nav-label">Clinic</div>

                <NavLink
                  to="/portal/clinic-admin/clinic-profile"
                  className={({ isActive }) =>
                    cls("nav-item", isActive && "active")
                  }
                  onClick={closeMobileMenu}
                  title="Clinic Profile"
                >
                  <span className="nav-item-label">Clinic Profile</span>
                </NavLink>

                {/* Activity Log and Settings intentionally hidden until real pages/actions exist. */}
              </div>
            </nav>

            <div
              className="clinic-admin-sidebar-bottom"
              aria-label="Clinic Admin account and portal actions"
            >
              {user.email ? (
                <div className="clinic-admin-signedin">
                  <div className="clinic-admin-signedin-label">
                    Signed in as
                  </div>
                  <div className="clinic-admin-signedin-email">
                    {user.email}
                  </div>
                </div>
              ) : null}

              <div className="clinic-admin-sidebar-bottom-card clinic-admin-sidebar-bottom-card--actions">
                <div className="clinic-admin-sidebar-actions-title">
                  Portal actions
                </div>

                <div className="clinic-admin-sidebar-actions-grid">
                  <NavLink
                    to="/public"
                    className="clinic-admin-side-link"
                    onClick={closeMobileMenu}
                    title="Public Site"
                  >
                    <span className="clinic-admin-side-link-label">
                      Public Site
                    </span>
                  </NavLink>

                  {hasMultipleRoles && (
                    <NavLink
                      to="/portal/select-role"
                      className="clinic-admin-side-link"
                      onClick={closeMobileMenu}
                      title="Switch Role"
                    >
                      <span className="clinic-admin-side-link-label">
                        Switch Role
                      </span>
                    </NavLink>
                  )}

                  <a
                    href={signOutHref}
                    className="clinic-admin-side-link clinic-admin-side-link--primary"
                    onClick={closeMobileMenu}
                    title="Sign Out"
                  >
                    <span className="clinic-admin-side-link-label">
                      Sign Out
                    </span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </aside>

        <div
          className={cls(
            "clinic-admin-main",
            sidebarCollapsed && "clinic-admin-main--sidebar-collapsed"
          )}
        >
          <main className="clinic-admin-content" aria-label="Portal content">
            <Outlet />
          </main>
        </div>
      </div>
    </ToastProvider>
  );
}